import { OfferItem, VIP_NUMBER_KEYS, VIP_PACKS_PAGE, VipBenefits, isRandomItem, periodOf, vipBenefits, vipTierProgress } from "@highmarch/domain";
import type { Deals } from "./deals";
import type { DealsContext, OfferView } from "./dealsTypes";

/**
 * The VIP page's view (docs/SHOP.md §8; Kingshot's VIP screen): the level and XP bar, today's VIP XP claim and its streak, every
 * level's benefits for the level browser (with what each level adds), the free daily bundle and each level's Special
 * Pack, all from the `vip` section's VipRecord (Deals.vipRecord). The web draws it (VIP-UI); the pack is bought through
 * Deals.prepare / PurchaseService, the XP claimed through Deals.claimVipXp and the bundle through Deals.claimVipBundle.
 */

export type VipNumberKey = (typeof VIP_NUMBER_KEYS)[number];
export type VipPackState = "hidden" | "locked" | "onSale" | "purchased";

export interface VipLevelView {
  readonly level: number;
  /** The XP threshold of this level. */
  readonly xp: number;
  readonly benefits: VipBenefits;
  /** The benefits this level raises over the one before (the page's NEW markers). */
  readonly raised: readonly VipNumberKey[];
  /** The level's Special Pack on sale (null when locked, bought or hidden: paid offers off, or a no-random storefront). */
  readonly pack: OfferView | null;
  readonly packState: VipPackState;
}

export interface VipPageView {
  readonly level: number;
  readonly xp: number;
  /** The next level's threshold; null at VIP 12 ("Maxed"). */
  readonly nextAt: number | null;
  readonly fraction: number;
  /** The streak day today's claim counts, claimed or not (docs/SHOP.md §8.2: a missed day starts it over at 1). */
  readonly streakDays: number;
  /** Today's VIP XP claim: what it pays (or paid) and whether it waits. */
  readonly daily: { readonly xp: number; readonly ready: boolean };
  /** What tomorrow's claim pays if today's is claimed and no day is missed. */
  readonly tomorrowXp: number;
  /** Today's free bundle at the current level: its items, whether it can be claimed, and the real reset time (00:00 UTC). */
  readonly bundle: { readonly items: readonly OfferItem[]; readonly ready: boolean; readonly endsAt: number };
  /** Levels 1..12 for the level browser. */
  readonly levels: readonly VipLevelView[];
}

export function vipPageView(deals: Deals, now: number, ctx: DealsContext): VipPageView {
  const B = deals.balanceData, table = B.vip, rec = deals.vipRecord, xp = rec.xp, p = vipTierProgress(table, xp);
  const packs = B.offers.pages.find((pg) => pg.id === VIP_PACKS_PAGE)?.offers ?? [];
  // A pack holding a random item is never sold in a no-random storefront (offers.json), so it is hidden there at every
  // level, locked and bought ones included, not only while on sale (Deals.view hides just those).
  const noRandom = ctx.storefront !== null && B.offers.noRandomStorefronts.includes(ctx.storefront);
  const levels = table.levels.slice(1).map((l, i): VipLevelView => {
    const level = i + 1, prev = table.levels[level - 1];
    const def = packs.find((o) => o.vipLevel === level);
    const pack = def ? deals.view(def.id, now, ctx) : null;
    const bought = def ? deals.state.remaining(def.id, def.limit, "once", now) <= 0 : false;
    const hiddenHere = !def || !ctx.paidAllowed || (noRandom && def.items.some(isRandomItem));
    const packState: VipPackState = hiddenHere ? "hidden" : bought ? "purchased" : p.level < level ? "locked" : pack ? "onSale" : "hidden";
    return { level, xp: l.xp, benefits: l, raised: VIP_NUMBER_KEYS.filter((k) => l[k] > prev[k]), pack, packState };
  });
  const today = vipBenefits(table, xp);
  return {
    level: p.level, xp, nextAt: p.nextAt, fraction: p.fraction, streakDays: rec.todayStreak(now),
    daily: { xp: rec.todayXp(table, now), ready: rec.claimReady(now) }, tomorrowXp: rec.tomorrowXp(table, now),
    bundle: { items: today.dailyBundle, ready: rec.bundleReady(table, now), endsAt: periodOf("daily", now).endsAt ?? now },
    levels,
  };
}
